import { Columns } from '@bedrock-layout/columns'
import { useIsClient } from '@okam/shared-ui/src'
import Link from 'next/link'
import Router from 'next/router'
import { Item } from 'react-stately'
import { ReactSVG } from 'react-svg'
import tw, { css } from 'twin.macro' // eslint-disable-line
import { useMediaQuery } from 'usehooks-ts'
import BackgroundPattern from '../BackgroundPattern'
import Button from '../Button'
import FlavourButton from '../FlavourButton'
import FoldWrapper from '../FoldWrapper'
import Gallery from '../Gallery'
import Heading from '../Heading'
import Img from '../Img'
import LottiePlayer from '../Lottie'
import Paragraph from '../Paragraph'
import SelectFilter from '../SelectFilter'
import WysiwygBlock from '../WysiwygBlock'
import type { TProductHero } from './interface'
import { bgPatternStyles, selectFilterContainerStyles } from './styles'

const ProductHero = (props: TProductHero) => {
  const {
    additionalImages,
    backgroundPatternUrl,
    claims,
    colorScheme,
    description,
    featuredImage,
    lottieUrl,
    sizes,
    title,
    flavorItems,
    flavor,
    buttons,
    selectAriaLabel,
    onAddToCartPressed,
    onSelectionChanged,
    onWhereToFindUsPressed,
  } = props

  const { isClient } = useIsClient()
  const isDesktop = useMediaQuery('(min-width: 1536px)')

  return (
    <FoldWrapper tw="p-6 relative z-10">
      <Columns columns={1} tw="items-center gap-6">
        <div tw="lg:w-7/12 xl:w-4/12">
          <Heading as="h1" variant="h4" tw=" mx-auto mb-2 text-3xl text-center font-semibold md:max-w-lg">
            {title}
          </Heading>
          {sizes && (
            <Paragraph as="p" variant="p" tw="mx-auto text-center md:max-w-lg">
              {sizes}
            </Paragraph>
          )}
        </div>
        <div tw="flex flex-wrap items-center justify-between gap-6 overflow-x-visible">
          {isClient && (
            <div tw="py-6 lg:px-12 px-12 w-full h-full flex items-center justify-center">
              {flavor && flavorItems && flavorItems.length > 0 && !isDesktop ? (
                <div css={selectFilterContainerStyles}>
                  <SelectFilter
                    bgColor={colorScheme?.accent ?? 'black'}
                    hoverColor={colorScheme?.accentMuted ?? 'black'}
                    iconBgColor={colorScheme?.outline}
                    variant="flavour"
                    id="Flavors"
                    name="Flavors"
                    placeholderLabel={flavor?.title ?? ''}
                    value={`flavourButton-${flavor.slug}`}
                    popoverStyles={[tw`min-w-[19rem]`]}
                    shouldPopoverFlip={false}
                    ariaLabel={selectAriaLabel ?? ''}
                    allowOutsideScrolling
                    onSelectionChange={(itemKey) => {
                      const flavorItem = flavorItems?.find((item) => (itemKey as string).includes(item?.slug ?? ''))
                      const flavorSlug = flavorItem?.slug ?? '#'
                      Router.push(flavorSlug)
                      onSelectionChanged?.(flavorItem?.title ?? '', flavorItem?.titleFr ?? '', flavorSlug)
                    }}
                  >
                    {flavorItems?.map(({ title: flavorTitle, imageUrl, slug }) => {
                      return (
                        <Item key={`flavourButton-${slug}`} textValue={flavorTitle}>
                          <Link tw="flex gap-2 p-2 items-center justify-between text-white" href={slug ?? '#'}>
                            {imageUrl && (
                              <div
                                className="flavour-icon"
                                tw="rounded-full flex items-center justify-center aspect-square bg-white min-w-12"
                              >
                                <span
                                  css={css`
                                    ${tw`!rounded-full overflow-hidden`}

                                    svg,
                                    svg path {
                                      fill: ${colorScheme?.accent};
                                      stroke: ${colorScheme?.accent};
                                      ${tw`!bg-white !p-0`}
                                    }

                                    &,
                                    span,
                                    svg {
                                      ${tw`!w-full !h-full !transform-none max-w-[50px] max-h-[50px] !rounded-full`}
                                    }
                                  `}
                                >
                                  <span
                                    css={css`
                                      ${tw`!rounded-full overflow-hidden`}
                                      svg,
                                      svg path {
                                        stroke: ${colorScheme?.accent};
                                        ${tw`!bg-white !p-0`}
                                      }
                                      &,
                                      span,
                                      svg {
                                        ${tw`!w-full !h-full !transform-none max-w-[50px] max-h-[50px] !rounded-full`}
                                      }
                                    `}
                                  >
                                    <ReactSVG
                                      src={imageUrl ?? ''}
                                      // eslint-disable-next-line react/no-unstable-nested-components
                                      fallback={() => (
                                        <div tw="w-[40px] h-[40px] flex items-center justify-center">
                                          <div tw="rounded-full w-[40px] h-[40px]" />
                                        </div>
                                      )}
                                    />
                                  </span>
                                </span>
                              </div>
                            )}
                            <span>{flavorTitle}</span>
                            <div className="flavor-button-spacer" />
                          </Link>
                        </Item>
                      )
                    })}
                  </SelectFilter>
                </div>
              ) : (
                <div tw="2xl:absolute 2xl:max-h-[616px] 2xl:min-w-[250px] left-12 top-40 flex flex-col gap-8">
                  {flavorItems?.map((item) => {
                    const { id, ...rest } = item
                    return (
                      <div key={`flavourButton-${id}`} {...rest}>
                        <FlavourButton
                          onClick={() => onSelectionChanged?.(item?.title ?? '', item.titleFr ?? '', item.slug ?? '#')}
                          link={item?.slug ?? '#'}
                          title={item?.title ?? ''}
                          ariaLabel={item?.title ?? ''}
                          selected={item.title === flavor?.title}
                        >
                          <span
                            css={css`
                              ${tw`!rounded-full overflow-hidden`}
                              svg,
                              svg path {
                                fill: ${colorScheme?.accent};
                                stroke: ${colorScheme?.accent};
                              }
                              &,
                              span,
                              svg {
                                ${tw`!w-full !h-full !transform-none max-w-[35px] max-h-[35px] !rounded-full`}
                              }
                            `}
                          >
                            <ReactSVG
                              src={item?.imageUrl ?? ''}
                              // eslint-disable-next-line react/no-unstable-nested-components
                              fallback={() => (
                                <div tw="w-[40px] h-[40px] flex items-center justify-center">
                                  <div tw="rounded-full w-[40px] h-[40px]" />
                                </div>
                              )}
                            />
                          </span>
                        </FlavourButton>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
          <div tw="hidden xl:block w-3/12" />
          {backgroundPatternUrl && (
            <BackgroundPattern
              css={bgPatternStyles}
              url={backgroundPatternUrl}
              clips={{
                '0px': '27rem',
                '768px': '24rem',
                '1024px': '22rem',
                '1440px': '38rem',
              }}
            />
          )}
          {(featuredImage || additionalImages) && (
            <Gallery
              tw="w-full lg:w-7/12 xl:w-4/12 lg:h-auto"
              slides={[featuredImage && featuredImage, ...(additionalImages ?? []).map((image) => image)].map(
                (image) => ({
                  id: image?.id,
                  child: (
                    <div tw="flex justify-center" key={`animalImage-${image?.id}`}>
                      <Img
                        src={`${image?.src}`}
                        alt={image?.description ?? 'Animal image'}
                        width={400}
                        height={400}
                        tw="max-h-[475px] object-contain"
                      />
                    </div>
                  ),
                }),
              )}
              color={colorScheme?.outline}
            >
              {lottieUrl && (
                <div tw="absolute z-10 left-[0rem] -bottom-20 lg:-left-20">
                  <LottiePlayer src={lottieUrl} width="20rem" autoplay loop />
                </div>
              )}
            </Gallery>
          )}
          {(buttons?.findUs || buttons?.buyMondou || buttons?.buyRen) && isClient && (
            <div tw="flex items-center justify-center flex-wrap w-full gap-4 lg:order-4">
              {buttons.buyMondou && buttons.buyMondou.link && (
                <Button
                  tw="text-white basis-full max-w-xs lg:basis-1/2"
                  backgroundColor={colorScheme?.accent ?? 'black'}
                  hoverColor={colorScheme?.accentMuted ?? 'black'}
                  as="a"
                  handlePress={() => onAddToCartPressed?.()}
                  target="_blank"
                  rel="noopener noreferrer"
                  href={buttons.buyMondou.link}
                >
                  {buttons.buyMondou.label}
                </Button>
              )}
              {buttons.buyRen && buttons.buyRen.link && (
                <Button
                  tw="text-white basis-full max-w-xs lg:basis-1/2"
                  backgroundColor={colorScheme?.accent ?? 'black'}
                  hoverColor={colorScheme?.accentMuted ?? 'black'}
                  as="a"
                  handlePress={() => onAddToCartPressed?.()}
                  target="_blank"
                  rel="noopener noreferrer"
                  href={buttons.buyRen.link}
                >
                  {buttons.buyRen.label}
                </Button>
              )}
              {buttons.findUs && (
                <Button
                  tw="basis-full max-w-xs lg:basis-1/2"
                  buttonStyle="outline"
                  backgroundColor={colorScheme?.background}
                  outlineColor={colorScheme?.outline}
                  hoverColor={colorScheme?.outline}
                  as="a"
                  handlePress={() => onWhereToFindUsPressed?.()}
                  href={buttons.findUs.link ?? '#'}
                >
                  {buttons.findUs.label}
                </Button>
              )}
            </div>
          )}
          {(description || claims) && (
            <div tw="flex flex-col items-center p-8 gap-8 lg:order-3 lg:w-4/12 xl:items-start xl:w-3/12">
              {description && <WysiwygBlock content={description} />}
              {claims && (
                <div tw="flex flex-col gap-3">
                  {claims.map((claim) => (
                    <span key={claim?.id} tw="flex gap-2 lg:gap-4">
                      <span
                        css={css`
                          svg {
                            width: 24px;
                            height: 24px;
                            fill: ${colorScheme?.accent};
                          }
                        `}
                      >
                        <ReactSVG
                          src={claim?.icon?.src ?? ''}
                          width={24}
                          height={24}
                          // eslint-disable-next-line react/no-unstable-nested-components
                          fallback={() => (
                            <div tw="w-6 h-6 flex items-center justify-center">
                              <div
                                tw="rounded-full w-3 h-3"
                                css={css`
                                  background-color: ${colorScheme?.accent};
                                `}
                              />
                            </div>
                          )}
                        />
                      </span>
                      <Paragraph
                        as="p"
                        variant="p"
                        css={css`
                          & {
                            color: ${colorScheme?.accent};
                          }
                        `}
                      >
                        {claim?.content}
                      </Paragraph>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </Columns>
    </FoldWrapper>
  )
}

export default ProductHero
